import { Controller } from '@hotwired/stimulus'

export default class extends Controller {
  static targets = [
    'sex', 'totalT', 'freeT', 'shbg',
    'totalTDisplay', 'totalTDot', 'totalTStatus',
    'freeTDisplay', 'freeTDot', 'freeTStatus',
    'shbgDisplay', 'shbgDot', 'shbgStatus',
    'labRange', 'optimalRange',
    'statusContainer', 'statusDot', 'statusName',
    'statusDescription', 'recommendation'
  ]

  connect() {
    this.calculate()
  }

  calculate() {
    const sex = this.sexTargets.find((r) => r.checked)?.value || 'male'
    const totalT = parseFloat(this.totalTTarget.value) || 0
    const freeT = parseFloat(this.freeTTarget.value) || 0
    const shbg = parseFloat(this.shbgTarget.value) || 0

    // Update ranges display
    const ranges = this.getRanges(sex)
    this.labRangeTarget.textContent = ranges.totalTLab
    this.optimalRangeTarget.textContent = ranges.totalTOptimal

    // Update individual marker displays
    this.updateTotalT(totalT, sex)
    this.updateFreeT(freeT, sex)
    this.updateShbg(shbg, sex)

    // Generate overall assessment if we have at least total T
    if (totalT > 0) {
      this.updateOverallAssessment(totalT, freeT, shbg, sex)
    }
  }

  getRanges(sex) {
    if (sex === 'female') {
      return {
        totalTLab: '15-70 ng/dL',
        totalTOptimal: '40-60 ng/dL',
        freeTLab: '0.3-1.9 pg/mL',
        freeTOptimal: '1.5-3.0 pg/mL',
        shbgLab: '18-144 nmol/L',
        shbgOptimal: '40-80 nmol/L'
      }
    }
    return {
      totalTLab: '300-1000 ng/dL',
      totalTOptimal: '600-900 ng/dL',
      freeTLab: '8.7-25.1 pg/mL',
      freeTOptimal: '15-25 pg/mL',
      shbgLab: '10-57 nmol/L',
      shbgOptimal: '20-40 nmol/L'
    }
  }

  getThresholds(sex) {
    if (sex === 'female') {
      return {
        totalT: { veryLow: 15, low: 40, optimalLow: 40, optimalHigh: 60, high: 70 },
        freeT: { veryLow: 0.3, low: 1.5, optimalLow: 1.5, optimalHigh: 3.0, high: 1.9 },
        shbg: { low: 18, optimalLow: 40, optimalHigh: 80, high: 144 }
      }
    }
    return {
      totalT: { veryLow: 300, low: 600, optimalLow: 600, optimalHigh: 900, high: 1000 },
      freeT: { veryLow: 8.7, low: 15, optimalLow: 15, optimalHigh: 25, high: 25.1 },
      shbg: { low: 10, optimalLow: 20, optimalHigh: 40, high: 57 }
    }
  }

  updateTotalT(value, sex) {
    if (value <= 0) {
      this.totalTDisplayTarget.textContent = '--'
      this.totalTDotTarget.className = 'w-4 h-4 bg-gray-300 mt-2'
      this.totalTStatusTarget.textContent = 'Enter your Total T value'
      return
    }

    this.totalTDisplayTarget.textContent = value

    const thresholds = this.getThresholds(sex).totalT
    const result = this.interpretTotalT(value, thresholds)
    this.totalTDotTarget.className = `w-4 h-4 bg-${result.color} mt-2`
    this.totalTStatusTarget.textContent = result.status
  }

  interpretTotalT(value, thresholds) {
    if (value < thresholds.veryLow) {
      return { color: 'red-500', status: 'Below standard range - significantly low' }
    }
    if (value < thresholds.low) {
      return { color: 'yellow-500', status: 'Within standard range but below optimal' }
    }
    if (value >= thresholds.optimalLow && value <= thresholds.optimalHigh) {
      return { color: 'green-500', status: 'Optimal functional medicine range' }
    }
    if (value > thresholds.high) {
      return { color: 'orange-500', status: 'Above standard range - may warrant evaluation' }
    }
    return { color: 'green-400', status: 'Within reference range' }
  }

  updateFreeT(value, sex) {
    if (value <= 0) {
      this.freeTDisplayTarget.textContent = '--'
      this.freeTDotTarget.className = 'w-4 h-4 bg-gray-300 mt-2'
      this.freeTStatusTarget.textContent = 'Optional: Enter Free T for deeper insight'
      return
    }

    this.freeTDisplayTarget.textContent = value

    const thresholds = this.getThresholds(sex).freeT
    const result = this.interpretFreeT(value, thresholds)
    this.freeTDotTarget.className = `w-4 h-4 bg-${result.color} mt-2`
    this.freeTStatusTarget.textContent = result.status
  }

  interpretFreeT(value, thresholds) {
    if (value < thresholds.veryLow) {
      return { color: 'red-500', status: 'Below standard range - low bioavailable T' }
    }
    if (value < thresholds.low) {
      return { color: 'yellow-500', status: 'Within standard range but suboptimal' }
    }
    if (value >= thresholds.optimalLow && value <= thresholds.optimalHigh) {
      return { color: 'green-500', status: 'Optimal functional medicine range' }
    }
    if (value > thresholds.high) {
      return { color: 'orange-500', status: 'Above standard range' }
    }
    return { color: 'green-400', status: 'Within reference range' }
  }

  updateShbg(value, sex) {
    if (value <= 0) {
      this.shbgDisplayTarget.textContent = '--'
      this.shbgDotTarget.className = 'w-4 h-4 bg-gray-300 mt-2'
      this.shbgStatusTarget.textContent = 'Optional: SHBG affects testosterone availability'
      return
    }

    this.shbgDisplayTarget.textContent = value

    const thresholds = this.getThresholds(sex).shbg
    const result = this.interpretShbg(value, thresholds)
    this.shbgDotTarget.className = `w-4 h-4 bg-${result.color} mt-2`
    this.shbgStatusTarget.textContent = result.status
  }

  interpretShbg(value, thresholds) {
    if (value < thresholds.low) {
      return { color: 'yellow-500', status: 'Low SHBG - may indicate insulin resistance' }
    }
    if (value >= thresholds.optimalLow && value <= thresholds.optimalHigh) {
      return { color: 'green-500', status: 'Optimal range for testosterone availability' }
    }
    if (value > thresholds.high) {
      return { color: 'orange-500', status: 'Elevated - may reduce free testosterone' }
    }
    return { color: 'green-400', status: 'Within reference range' }
  }

  updateOverallAssessment(totalT, freeT, shbg, sex) {
    const thresholds = this.getThresholds(sex)
    let result

    // Primary assessment based on Total T
    if (totalT < thresholds.totalT.veryLow) {
      result = {
        name: 'LOW TESTOSTERONE',
        color: 'red-500',
        borderColor: 'border-red-500',
        bgColor: 'bg-red-50',
        description: 'Your Total Testosterone is below the standard reference range. ' +
          'This may be associated with significant symptoms and warrants medical evaluation.',
        recommendation: 'Consult with an endocrinologist or hormone specialist. Repeat testing to confirm, ' +
          'and evaluate underlying causes. Additional tests like LH, FSH, and prolactin may be needed.'
      }
    } else if (totalT < thresholds.totalT.low) {
      result = {
        name: 'SUBOPTIMAL',
        color: 'yellow-500',
        borderColor: 'border-yellow-500',
        bgColor: 'bg-yellow-50',
        description: 'Your Total T is within the standard range but below functional medicine optimal levels. ' +
          'You may experience subtle symptoms of low testosterone.',
        recommendation: 'Focus on lifestyle optimization: sleep, stress management, resistance training, ' +
          'and nutrition. Retest in 3 months. Consider working with a functional medicine practitioner.'
      }
    } else if (totalT >= thresholds.totalT.optimalLow && totalT <= thresholds.totalT.optimalHigh) {
      result = {
        name: 'OPTIMAL',
        color: 'green-500',
        borderColor: 'border-green-500',
        bgColor: 'bg-green-50',
        description: 'Your Total Testosterone is in the optimal functional medicine range. ' +
          'This level is associated with good energy, mood, and body composition.',
        recommendation: 'Maintain your current healthy lifestyle. Continue with regular exercise, ' +
          'quality sleep, stress management, and balanced nutrition.'
      }
    } else if (totalT > thresholds.totalT.high) {
      result = {
        name: 'ELEVATED',
        color: 'orange-500',
        borderColor: 'border-orange-500',
        bgColor: 'bg-orange-50',
        description: 'Your Total Testosterone is above the standard reference range. ' +
          'This may warrant evaluation, especially if not on testosterone therapy.',
        recommendation: 'Discuss with your healthcare provider. Rule out adrenal or testicular tumors ' +
          'if applicable. If on TRT, dosing may need adjustment.'
      }
    } else {
      result = {
        name: 'ADEQUATE',
        color: 'green-400',
        borderColor: 'border-green-400',
        bgColor: 'bg-green-50',
        description: 'Your Total Testosterone is within the reference range. ' +
          'Depending on symptoms, optimization may still be beneficial.',
        recommendation: 'If experiencing symptoms, focus on lifestyle factors. ' +
          'Consider complete hormone panel including Free T and SHBG for a fuller picture.'
      }
    }

    // Modify assessment if Free T data is available
    if (freeT > 0) {
      if (freeT < thresholds.freeT.veryLow && totalT >= thresholds.totalT.veryLow) {
        result.description += ' Note: Your Free T is low despite adequate Total T, which may explain symptoms.'
        result.recommendation = 'Low Free T with normal Total T often indicates high SHBG. ' +
          `Address factors that elevate SHBG (liver health, thyroid, medications). ${result.recommendation}`
      }
      if (freeT >= thresholds.freeT.optimalLow && freeT <= thresholds.freeT.optimalHigh && result.name !== 'OPTIMAL') {
        result.description += ' Good news: Your Free T is in the optimal range.'
      }
    }

    // Add SHBG context if available
    if (shbg > 0) {
      if (shbg > thresholds.shbg.high) {
        result.description += ' Elevated SHBG may be reducing your free testosterone availability.'
      } else if (shbg < thresholds.shbg.low) {
        result.description += ' Low SHBG may indicate insulin resistance or metabolic issues.'
      }
    }

    this.updateDisplay(result)
  }

  updateDisplay(result) {
    this.statusDotTarget.className = `w-4 h-4 bg-${result.color}`
    this.statusNameTarget.textContent = result.name
    this.statusDescriptionTarget.textContent = result.description
    this.recommendationTarget.textContent = result.recommendation
    this.statusContainerTarget.className = `p-6 ${result.borderColor} border-2 ${result.bgColor} mb-6`
  }
}
